import { ModelRow } from "@mcleod/core";
import { AutogenLayoutOrderDataFusionSummary } from "./autogen/AutogenLayoutOrderDataFusionSummary";
import { ClickEvent, Layout, SlideoutDecorator, Table, TableRow } from "@mcleod/components";
import { ShipmentStatusSlideout } from "./ShipmentStatusSlideout";

export class OrderDataFusionSummary extends AutogenLayoutOrderDataFusionSummary {
    private _movementId: string;
    private _orderId: string;
    private _customerId: string;
    private _companyId: string;
    private _displayingData: boolean = true;
    private _pbLtl: boolean;
    private _shipperStopId: string;
    private _consigneeStopId: string;
    private _hasIntercompany: boolean;
    private _hasICError: boolean;

    setEdiData(responseData: ModelRow) {
        const hasEdi: boolean = responseData?.["has_edi"] ?? false;
        const hasLogistics: boolean = responseData?.["has_logistics"] ?? false;
        const hasOutStatuses: boolean = responseData?.["has_statuses"] ?? false;
        const hasInStatuses: boolean = responseData?.["has_log_statuses"] ?? false;
        const hasInTenders: boolean = responseData?.["has_in_tenders"] ?? false;
        const hasLogTenders: boolean = responseData?.["has_log_tenders"] ?? false;
        const inTenderCount: number = responseData?.["in_tender_count"] ?? 0;
        const outTenderCount: number = responseData?.["out_tender_count"] ?? 0;
        const inTenderError: boolean = responseData?.["in_tender_error"] ?? false;
        const outTenderError: boolean = responseData?.["out_tender_error"] ?? false;
        this._pbLtl = responseData?.["pbltl_dispatched"] ?? false;

        if (!this._pbLtl && (!hasEdi && !hasLogistics || (!hasOutStatuses && !hasInStatuses && !hasLogTenders && (inTenderCount == 0 && outTenderCount == 0)))) {
            this.displayingData = false;
            return;
        }
        else if( !hasLogTenders && !hasInTenders && !hasOutStatuses && !hasInStatuses ){
            this.displayingData = false;
            return;
        }

        this.displayingData = true;

        // Show or hide the outbound tender count
        if (hasLogTenders) {
            this.labelOutboundTenders.visible = true;
            this.labelOutboundTenderCount.visible = true;
            this.labelOutboundTenderCount.text = outTenderCount.toString();
            if (outTenderError) {
                this.labelOutboundTenderCount.color = "error";
                this.imageOutboundTenderError.visible = true;
            } else {
                this.labelOutboundTenderCount.color = "primary.dark";
                this.imageOutboundTenderError.visible = false;
            }
        } else {
            this.labelOutboundTenders.visible = false;
            this.labelOutboundTenderCount.visible = false;
            this.imageOutboundTenderError.visible = false;
        }

        // Show or hide the inbound tender count
        if (inTenderCount > 0) {
            this.labelInboundTenders.visible = true;
            this.labelInboundTenderCount.visible = true;
            this.labelInboundTenderCount.text = inTenderCount.toString();
            if (inTenderError) {
                this.labelInboundTenderCount.color = "error";
                this.imageInboundTenderError.visible = true;
            } else {
                this.labelInboundTenderCount.color = "primary.dark";
                this.imageInboundTenderError.visible = false;
            }
        } else {
            this.labelInboundTenders.visible = false;
            this.labelInboundTenderCount.visible = false;
            this.imageInboundTenderError.visible = false;
        }

        // Show or hide the outbound shipment status count
        if (hasOutStatuses) {
            const outboundRows: ModelRow[] = responseData?.["edi_statuses_out"];
            this.labelOutboundShipmentStatuses.visible = true;
            this.labelOutboundShipmentStatusCount.visible = true;
            if (outboundRows && outboundRows.length > 0) {
                this.labelOutboundShipmentStatusCount.text = outboundRows.length.toString();
                this.labelOutboundShipmentStatusCount.color = "primary.dark";
                this.imageOutboundShipmentStatusError.visible = false;
                const hasErrors: boolean = outboundRows.some(row =>
                    row['status_error'] > 0
                );
                this.labelOutboundShipmentStatusCount.color = hasErrors ? "error" : "primary.dark";
                this.imageOutboundShipmentStatusError.visible = hasErrors;
            } else {
                this.labelOutboundShipmentStatusCount.text = "0";
                this.labelOutboundShipmentStatusCount.color = "primary.dark";
                this.imageOutboundShipmentStatusError.visible = false;
                this.labelOutboundShipmentStatuses.visible = false;
                this.labelOutboundShipmentStatusCount.visible = false;
            }
        } else {
            this.labelOutboundShipmentStatuses.visible = false;
            this.labelOutboundShipmentStatusCount.visible = false;
            this.imageOutboundShipmentStatusError.visible = false;
        }

        // Show or hide the inbound shipment status count
        if (hasInStatuses || this._pbLtl) {
            const inboundRows: ModelRow[] = responseData?.["edi_statuses_in"];
            this.labelInboundShipmentStatuses.visible = true;
            this.labelInboundShipmentStatusCount.visible = true;
            if (inboundRows?.length > 0) {
                this.labelInboundShipmentStatusCount.text = inboundRows.length.toString();
                this.labelInboundShipmentStatusCount.color = "primary.dark";
                this.imageInboundShipmentStatusError.visible = false;
                const hasErrors: boolean = inboundRows.some(row =>
                    row['status_error'] > 0
                );
                this.labelInboundShipmentStatusCount.color = hasErrors ? "error" : "primary.dark";
                this.imageInboundShipmentStatusError.visible = hasErrors;
            } else {
                this.labelInboundShipmentStatusCount.text = "0";
                this.labelInboundShipmentStatusCount.color = "primary.dark";
                this.imageInboundShipmentStatusError.visible = false;
            }
        } else {
            this.labelInboundShipmentStatuses.visible = false;
            this.labelInboundShipmentStatusCount.visible = false;
            this.imageInboundShipmentStatusError.visible = false;
        }
    }

    dataFusionCompCountOnClick(event: ClickEvent) {
        const selectedComp = event.target.id;

        if (this.orderId && this.customerId) {
            const layout = Layout.getLayout("lme/dispatch/ShipmentStatusSlideout", {
                //maxWidth: 900,
                width: window.innerWidth * .80,
                backgroundColor: "defaultBackground",
                fillHeight: true,
                paddingBottom: 50
            }) as ShipmentStatusSlideout;
            layout.pbLtl = this._pbLtl;
            const decorator = new SlideoutDecorator({
                layout: layout,
                title: "DataFusion - Order " + this.orderId,
                fillVerticalSpace: true,
                addlComponents: [layout.saveButton],
                layoutLoadListeners: (event) => {
                    layout.sourceEdistatus.search({ order_id: this.orderId });
                    layout.sourceEdiOrder.search({ order_id: this.orderId, movement_id: this.movementId });
                    layout.sourceEdiMovementCarrier.search({ movement_id: this.movementId });
                    layout.sourceEdiCustomer.search({ customer_id: this.customerId, transaction_type: "S" });
                    if(this.hasIntercompany) {
                        layout.sourceEdiMovementOrderIc.search({ order_id: this.orderId, original_order: "Y", in_movement_id: this.movementId});
                    }
                },
                doAfterSlideIn: (decorator: SlideoutDecorator) => {
                    let selIndex: number = 0;
                    layout.saveButton.addClickListener(async event => {
                        if (decorator.validateSimple()) {
                            await layout.saveChanges();
                        }
                    });

                    if (selectedComp == this.imageIntercompany.id || selectedComp == this.imageIntercompanyError.id ) {
                        selIndex = layout.tabsetDataFusion.indexOf(layout.tabIntercompany);
                    } else if (selectedComp == this.labelInboundShipmentStatusCount.id) {
                        selIndex = layout.tabsetDataFusion.indexOf(layout.tabInbound);
                    } else if (selectedComp == this.labelInboundTenderCount.id) {
                        selIndex = layout.tabsetDataFusion.indexOf(layout.tabInboundTender);
                    } else if (selectedComp == this.labelOutboundShipmentStatusCount.id) {
                        selIndex = layout.tabsetDataFusion.indexOf(layout.tabOutbound);
                    } else if (selectedComp == this.labelOutboundTenderCount.id) {
                        selIndex = layout.tabsetDataFusion.indexOf(layout.tabOutboundTender);
                    }
                    layout.tabsetDataFusion.selectedIndex = selIndex;
                },
                onClose: () => {
                    decorator.slideOut();
                },
                doAfterSlideOut: () => {
                    const errorsCallbackFunction = (row: TableRow) => { return row.data.get("error_count") === "0 Errors" };
                    if (selectedComp == this.labelInboundShipmentStatusCount.id) {
                        const currentCount: number = parseInt(this.labelInboundShipmentStatusCount.text);
                        const layoutCount: number = layout.tableInbound.rows.length;
                        if (currentCount !== layoutCount) {
                            this.labelInboundShipmentStatusCount.text = layoutCount.toString();
                        }
                        const currentError: boolean = this.imageInboundShipmentStatusError.visible;
                        const layoutError: boolean = !layout.tableInbound.rows.every(errorsCallbackFunction);
                        if (currentError !== layoutError) {
                            this.imageInboundShipmentStatusError.visible = layoutError;
                            this.labelInboundShipmentStatusCount.color = layoutError ? "error" : "primary.dark";
                        }
                    } else if (selectedComp == this.labelOutboundShipmentStatusCount.id) {
                        const currentCount: number = parseInt(this.labelOutboundShipmentStatusCount.text);
                        const layoutCount: number = layout.tableOutbound.rows.length;
                        if (currentCount !== layoutCount) {
                            this.labelOutboundShipmentStatusCount.text = layoutCount.toString();
                        }
                        const currentError: boolean = this.imageOutboundShipmentStatusError.visible;
                        const layoutError: boolean = !layout.tableOutbound.rows.every(errorsCallbackFunction);
                        if (currentError !== layoutError) {
                            this.imageOutboundShipmentStatusError.visible = layoutError;
                            this.labelOutboundShipmentStatusCount.color = layoutError ? "error" : "primary.dark";
                        }
                    }
                }
            });

            layout.orderId = this.orderId;
            layout.movementId = this.movementId;
            layout.customerId = this.customerId;
            layout.companyId = this.companyId;
        }
    }

    get movementId(): string {
        return this._movementId;
    }

    set movementId(movementId: string) {
        this._movementId = movementId;
    }

    get orderId(): string {
        return this._orderId;
    }

    set orderId(orderId: string) {
        this._orderId = orderId;
    }

    get customerId(): string {
        return this._customerId;
    }

    set customerId(customerId: string) {
        this._customerId = customerId;
    }

    public get companyId(): string {
        return this._companyId;
    }

    public set companyId(value: string) {
        this._companyId = value;
    }

    get displayingData(): boolean {
        return this._displayingData;
    }

    set displayingData(displayingData: boolean) {
        this._displayingData = displayingData;
    }

    public get shipperStopId(): string {
        return this._shipperStopId;
    }

    public set shipperStopId(value: string) {
        this._shipperStopId = value;
    }

    public get consigneeStopId(): string {
        return this._consigneeStopId;
    }

    public set consigneeStopId(value: string) {
        this._consigneeStopId = value;
    }

    public get hasIntercompany(): boolean {
        return this._hasIntercompany;
    }

    public set hasIntercompany(value: boolean) {
        this._hasIntercompany = value;
    }

    public get hasICError(): boolean {
        return this._hasICError;
    }

    public set hasICError(value: boolean) {
        if(this.hasIntercompany) {
            this._hasICError = value;
            this.imageIntercompanyError.color = "primary";
            this.imageIntercompanyError.visible = value;
            this.imageIntercompany.visible = !value;
        } else {
            this.imageIntercompanyError.visible = false;
            this.imageIntercompany.visible = false;
        }

    }
}
